import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "../lib/api";
import { ArrowRight, Search, Clock } from "lucide-react";
import { motion } from "framer-motion";
import Spinner from "../components/Spinner";
import SEOHead from "../components/SEOHead";
import { useSettings } from "../context/SettingsContext";
import { getSeoPage } from "../utils/seoPages";

export default function Blog() {
  const [posts, setPosts] = useState([]);
  const [pillars, setPillars] = useState([]);
  const [activePillar, setActivePillar] = useState("");
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const { settings: s } = useSettings();
  const seo = getSeoPage(s, "/blog");

  useEffect(() => {
    Promise.all([
      api.get("/blog/posts" + (activePillar ? `?pillar=${encodeURIComponent(activePillar)}` : "")).then((r) => setPosts(r.data)),
      api.get("/blog/pillars").then((r) => setPillars(r.data)).catch(() => setPillars([])),
    ]).finally(() => setLoading(false));
  }, [activePillar]);

  const filtered = posts.filter((p) => `${p.title} ${p.seo_subtitle || ""} ${p.excerpt}`.toLowerCase().includes(q.toLowerCase()));

  return (
    <div data-testid="blog-page">
      <SEOHead
        title={seo.title}
        description={seo.description}
        url={typeof window !== "undefined" ? window.location.href : ""}
        type="website"
      />
      <section className="bg-white border-b border-emerald-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
          <p className="label">Le blog SF Learning</p>
          <h1 className="font-heading text-4xl sm:text-5xl font-black mt-2">Conseils, méthodes & <span className="text-brand-green">inspiration</span></h1>
          <p className="text-gray-600 mt-3 max-w-2xl">Des articles pour grandir au quotidien — extraits de nos formations, retours d'expérience, méthodes pratiques.</p>
          <div className="mt-6 grid grid-cols-1 md:grid-cols-[minmax(0,420px)_260px] gap-3 items-center max-w-3xl">
            <div className="relative">
              <Search className="w-5 h-5 absolute left-4 top-1/2 -translate-y-1/2 text-gray-400" />
              <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Rechercher un article..." className="input pl-11" data-testid="blog-search-input" />
            </div>
            <select
              value={activePillar}
              onChange={(e) => setActivePillar(e.target.value)}
              className="input bg-white cursor-pointer"
              data-testid="blog-pillar-select"
              aria-label="Filtrer les articles par pilier SEO"
            >
              <option value="">Tous les piliers SEO</option>
              {pillars.map((p) => (
                <option key={p.pillar} value={p.pillar}>{p.pillar}{p.count ? ` (${p.count})` : ""}</option>
              ))}
            </select>
          </div>
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {loading ? <Spinner /> : (
          filtered.length === 0 ? (
            <p className="text-center text-gray-500 py-12" data-testid="blog-empty">Aucun article trouvé.</p>
          ) : (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {filtered.map((p, i) => (
                <motion.article
                  key={p.post_id}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ delay: i * 0.05 }}
                  viewport={{ once: true }}
                  className="card overflow-hidden group flex flex-col"
                  data-testid={`blog-card-${p.slug}`}
                >
                  <Link to={`/blog/${p.slug}`}>
                    <div className="aspect-video overflow-hidden flex items-center justify-center px-4 pt-4 bg-white">
                      {p.cover_image && (
                        <img
                          src={p.cover_image}
                          alt={p.title}
                          className="max-w-full max-h-full object-contain rounded-2xl group-hover:scale-105 transition-transform duration-700"
                        />
                      )}
                    </div>
                  </Link>
                  <div className="p-6 flex flex-col flex-1">
                    <Link to={`/blog/${p.slug}`}>
                      <h3 className="font-heading text-xl font-bold mb-2 group-hover:text-brand-green leading-snug">{p.title}</h3>
                    </Link>
                    {p.seo_subtitle && <p className="text-sm font-semibold text-brand-green mb-2 line-clamp-2">{p.seo_subtitle}</p>}
                    <p className="text-sm text-gray-600 line-clamp-3 flex-1">{p.excerpt}</p>
                    <div className="flex items-center justify-between mt-5 text-xs text-gray-500">
                      <span className="inline-flex items-center gap-1"><Clock className="w-3.5 h-3.5" /> {p.published_at ? new Date(p.published_at).toLocaleDateString("fr-FR") : ""}</span>
                      <Link to={`/blog/${p.slug}`} className="font-semibold text-brand-green inline-flex items-center gap-1 hover:gap-2 transition-all" data-testid={`blog-read-${p.slug}`}>
                        Lire <ArrowRight className="w-4 h-4" />
                      </Link>
                    </div>
                  </div>
                </motion.article>
              ))}
            </div>
          )
        )}
      </section>
    </div>
  );
}
